/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { connect, useDispatch } from "react-redux";
import queryString from "query-string";
import _ from "lodash";
import { IoFunnel } from "react-icons/io5";
import { bindActionCreators } from "redux";
import { List, CellMeasurerCache, CellMeasurer } from "react-virtualized";

import { endOfDay, startOfDay } from "date-fns";
import { validateAuthRoles } from "../../../helpers";
import * as codeActions from "../../../actions/codeActions";
import * as PtoRequestActions from "../../../actions/ptoRequestActions";
import PtoRequestService from "../../../services/ptoRequestService";
import Loading from "../../common/Loading";
import PtoApprovalFilters from "./PtoApprovalFilters";
import PtoApprovalItem from "./PtoApprovalItem";
import HeaderWrapper from "../../common/HeaderWrapper";
import * as modalActions from "../../../actions/modalActions";
import ConfirmModal from "../../common/ConfirmModal";

import "./PtoApprovalPage.scss";

import {
  getFilterState,
  saveFilterState,
  customToast as toast
} from "../../../utils";

const PtoRequestApprovalPage = ({ auth, codes, actions, ptoBalance }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [needUpdate, setNeedUpdate] = useState(false);
  const [filters, setFilters] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [focusedInput, setFocusedInput] = useState(false);
  const [selectedPTO, setSelectedPTO] = useState(null);
  const cache = new CellMeasurerCache({
    fixedWidth: true,
    minHeight: 50
  });

  const dispatch = useDispatch();

  const closeModal = (id) => {
    dispatch(modalActions.hideModal(id));
  };

  const handleOpenModal = (id, selectedPTOItem) => {
    setSelectedPTO(selectedPTOItem);
    dispatch(modalActions.showModal(id));
  };

  const handleReset = useCallback(() => {
    setLoading(true);

    const options = codes.filter(
      (code) =>
        code.entity === "PTO Request" &&
        code.field === "status" &&
        code.order === 1
    );
    setFilters({
      status: options.map((option) => option._id),
      manager: auth && auth.user && [auth.user.id]
    });
    saveFilterState("PTOREQUEST_APPROVAL_FILTERS", {
      status: options.map((option) => option._id),
      manager: auth && auth.user && [auth.user.id]
    });

    setNeedUpdate(true);
    setLoading(false);
  }, [auth, codes, setLoading, setNeedUpdate]);

  const handleFetchCalls = useCallback(async () => {
    setLoading(true);
    try {
      setNeedUpdate(false);
      setData(null);
      const Filters = { ...filters };

      if (filters?.start_date) {
        Filters.start_date = startOfDay(
          new Date(filters.start_date)
        ).toISOString();
      }

      if (filters?.end_date) {
        Filters.end_date = endOfDay(new Date(filters.end_date)).toISOString();
      }

      const query = queryString.stringify(
        {
          ...Filters
        },
        {
          arrayFormat: "bracket"
        }
      );

      const result = await PtoRequestService.get(query);

      const sortedResult = _.orderBy(result, (o) => moment(o.start_date), [
        "asc"
      ]);
      setData(sortedResult);
      setLoading(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (!filters && auth && auth.user && auth.user._id && !loading) {
      let newFilters = {};
      if (!validateAuthRoles(auth, "Admin")) {
        newFilters = {
          ...getFilterState("PTOREQUEST_APPROVAL_FILTERS"),
          manager: [auth.user._id]
        };
      } else {
        newFilters = getFilterState("PTOREQUEST_APPROVAL_FILTERS");
      }
      setFilters(newFilters);
      saveFilterState("PTOREQUEST_APPROVAL_FILTERS", newFilters);
      setNeedUpdate(true);
    }

    if (needUpdate && !loading) {
      setNeedUpdate(false);
      handleFetchCalls();
    }

    if (!codes.length && !loading) {
      actions.loadCodes();
    }
  }, [
    actions,
    codes,
    needUpdate,
    loading,
    handleReset,
    setLoading,
    filters,
    auth,
    setFilters,
    handleFetchCalls
  ]);

  useEffect(() => {
    if (!ptoBalance) {
      actions.getPtoRequestBalance(auth.user._id);
    }
  }, [actions, ptoBalance, auth]);

  const handleSubmit = (e) => {
    e.preventDefault();
  };
  const handleChange = (name) => async (value) => {
    let newFilters = getFilterState("PTOREQUEST_APPROVAL_FILTERS");
    if (Array.isArray(value)) {
      newFilters = {
        ...newFilters,
        [name]: value.map((v) => v.value)
      };
    } else {
      newFilters = await Object.keys(newFilters).reduce((acc, curr) => {
        if (curr !== name) {
          acc[curr] = newFilters[curr];
        }
        return acc;
      }, {});
    }
    await setFilters(newFilters);
    await saveFilterState("PTOREQUEST_APPROVAL_FILTERS", newFilters);
  };

  const handleUpdate = async (request) => {
    const updatedRequest = {
      _id: request._id,
      status: request.status._id,
      initial_approval_by: request.initial_approval_by?._id || null,
      initial_approval_on: request.initial_approval_on,
      initial_approval_note: request.initial_approval_note,
      declined_by: request.declined_by?._id || null,
      declined_on: request.declined_on,
      declined_note: request.declined_note
    };
    await actions.updatePtoRequest(updatedRequest);
    setData(
      data.map((pto) =>
        pto._id.toString() === updatedRequest._id.toString()
          ? updatedRequest
          : pto
      )
    );
    setNeedUpdate(true);
  };

  const handleDecline = () => {
    const value = !selectedPTO.declined_on;
    const req = { ...selectedPTO };
    // find new status
    let newStatusOrder = 5;
    if (!value) {
      newStatusOrder = 1;
    }
    const statusCodes = codes.filter(
      (code) => code.entity === "PTO Request" && code.field === "status"
    );

    req.status = statusCodes.find(
      (code) =>
        code.entity === "PTO Request" &&
        code.field === "status" &&
        code.order === newStatusOrder
    );

    if (value) {
      req.declined_by = {
        _id: auth.user._id,
        full_name: auth.user.full_name
      };
      req.declined_on = new Date();
    } else {
      req.declined_by = null;
      req.declined_on = null;
    }

    const updatedData = data.map((item) => (item._id === req._id ? req : item));

    setData(updatedData);
    handleUpdate(req);
  };

  const handleChangeDate = (startDate, endDate) => {
    setFilters({
      ...filters,
      ...(startDate && { start_date: startDate }),
      ...(endDate && { end_date: endDate })
    });
    saveFilterState("PTOREQUEST_APPROVAL_FILTERS", {
      ...filters,
      ...(startDate && { start_date: startOfDay(new Date(startDate)) }),
      ...(endDate && { end_date: endOfDay(new Date(endDate)) })
    });
  };

  const rowRenderer = ({ index, key, parent, style }) => {
    const pto = data[index];
    return (
      <CellMeasurer
        cache={cache}
        columnIndex={0}
        key={key}
        rowIndex={index}
        parent={parent}
      >
        {({ registerChild }) => (
          <div
            ref={registerChild}
            key={pto._id}
            className="list-item"
            disabled={loading}
            style={style}
          >
            <PtoApprovalItem
              key={pto._id}
              request={pto}
              handleUpdate={handleUpdate}
              handleOpenModal={handleOpenModal}
              auth={auth}
              ptoData={ptoBalance}
              statusCodes={codes.filter(
                (code) =>
                  code.entity === "PTO Request" && code.field === "status"
              )}
            />
          </div>
        )}
      </CellMeasurer>
    );
  };

  return (
    <div>
      <HeaderWrapper
        breadcrumbs={[{ label: "PTO Requests" }]}
        onClickAction={[
          { action: () => setShowFilters(!showFilters), icon: <IoFunnel /> }
        ]}
      />
      <PtoApprovalFilters
        show={showFilters}
        filters={filters}
        handleChange={handleChange}
        handleReset={handleReset}
        loading={loading}
        handleSubmit={handleSubmit}
        auth={auth}
        handleChangeDate={handleChangeDate}
        onFocusChange={(e) => setFocusedInput(e)}
        focusedInput={focusedInput}
        onGetCallsClick={handleFetchCalls}
      />
      <div className="content-wrapper">
        {loading && (
          <div className="loading-loader-load-container">
            <Loading active={loading} />
          </div>
        )}
        {!loading && data && Array.isArray(data) && data?.length > 0 && (
          <List
            data={data}
            width={1}
            containerStyle={{
              width: "100%",
              maxWidth: "100%"
            }}
            style={{
              width: "100%"
            }}
            overscanRowCount={0}
            height={
              showFilters ? window.innerHeight - 270 : window.innerHeight - 140
            }
            rowCount={data.length}
            rowHeight={cache.rowHeight}
            rowRenderer={rowRenderer}
          />
        )}
        {!loading && (!data || (Array.isArray(data) && data.length === 0)) && (
          <div className="loading">
            <h4 style={{ opacity: 0.15, textAlign: "center" }}>
              No PTO requests found
            </h4>
          </div>
        )}
      </div>
      <ConfirmModal
        id="declineCompletedPTOModal"
        title="Decline PTO"
        body="Are you sure you want to decline this PTO Request that was marked as completed?"
        confirm={handleDecline}
        close={closeModal}
      />
    </div>
  );
};

PtoRequestApprovalPage.propTypes = {
  auth: PropTypes.object,
  codes: PropTypes.array,
  actions: PropTypes.object,
  ptoBalance: PropTypes.object
};
function mapStatesToProps(state) {
  return {
    auth: state.reducers.auth,
    codes: state.reducers.codes,
    ptoBalance: state.reducers.ptoBalance
  };
}
const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...codeActions,
      ...PtoRequestActions
    },
    dispatch
  )
});

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(PtoRequestApprovalPage);
